import { Component } from '@theme/component';

/**
 * A small utility for setting up an animated disclosure panel using inert and CSS display grid for height auto animation.
 *
 * This component works with any element that follows the details/summary pattern for revealing content.
 * Forms and other interactive elements don't need to live inside a details element, but can still use
 * the expanded pattern for accessibility and animation.
 *
 * The component relies on CSS display grid and inert attributes to create smooth height animations
 * when expanding/collapsing content areas.
 *
 * @class DisclosureCustom
 * @typedef {object} Refs
 * @property {HTMLElement} disclosureTrigger – The disclosure trigger element.
 * @property {HTMLElement} disclosureContent – The disclosure content element.
 *
 * @extends Component<Refs>
 */
class DisclosureCustom extends Component {
  requiredRefs = ['disclosureTrigger', 'disclosureContent'];

  /** @type {boolean | null} */
  #expandedPreference = null;

  connectedCallback() {
    super.connectedCallback();
    this.#expandedPreference = this.#isExpanded();
  }

  /**
   * Re-sync refs after section morphs, then restore the user's open/closed preference.
   * Cart updates remorph the drawer to the server markup (usually closed), which would
   * otherwise collapse an accordion the shopper just opened.
   */
  updatedCallback() {
    super.updatedCallback();

    if (this.#expandedPreference != null) {
      this.#applyExpanded(this.#expandedPreference);
    }
  }

  /**
   * Toggles the disclosure panel open/closed state.
   * Updates aria-expanded, aria-label attributes on the trigger and inert state on content.
   * Works with CSS grid animations to provide smooth height transitions.
   */
  toggleDisclosure = () => {
    const nextExpanded = !this.#isExpanded();
    this.#expandedPreference = nextExpanded;
    this.#applyExpanded(nextExpanded);
  };

  /**
   * @returns {boolean}
   */
  #isExpanded() {
    const { disclosureTrigger: trigger } = this.refs;
    return trigger?.getAttribute('aria-expanded') === 'true';
  }

  /**
   * Applies a consistent expanded/collapsed state to the trigger and content.
   * Clears any stuck mid-transition grid rows so expand/collapse stays reliable.
   * @param {boolean} expanded
   */
  #applyExpanded(expanded) {
    const { disclosureTrigger: trigger, disclosureContent: content } = this.refs;
    if (!(trigger instanceof HTMLElement) || !(content instanceof HTMLElement)) return;

    trigger.setAttribute('aria-expanded', String(expanded));
    trigger.setAttribute(
      'aria-label',
      `${expanded ? trigger.dataset.disclosureClose : trigger.dataset.disclosureOpen}`
    );

    // Keep IDL + attribute in sync; CSS keys off [inert].
    content.inert = !expanded;

    // Interrupted grid-template-rows transitions can leave the panel stuck at 0 height
    // (icon toggles, form stays clipped). Snap the track, then let CSS take over.
    content.style.transition = 'none';
    content.style.gridTemplateRows = expanded ? '1fr' : '0fr';
    // Force layout so the snap applies before re-enabling transitions.
    void content.offsetHeight;
    content.style.removeProperty('transition');
    content.style.removeProperty('grid-template-rows');
  }
}

if (!customElements.get('disclosure-custom')) {
  customElements.define('disclosure-custom', DisclosureCustom);
}
